import { TZDate } from '@date-fns/tz';
import { useRef } from 'react';

import { formatDate } from '@dynatrace-sdk/units';

import { field as monthFieldStyle } from '../Calendar.sty.js';
import { CalendarItem } from './CalendarItem.js';
import { useFormFieldContext } from '../../forms-core/hooks/useFormFieldContext.js';
import { useFormFieldMessagesContext } from '../../forms-core/hooks/useFormFieldMessagesContext.js';
import { useMonth } from '../hooks/useMonth.js';

/** @internal */
export const Month = ({ date }: { date: TZDate }) => {
  // Reference of the month button, needed to pass to the hook for proper focus management
  const itemRef = useRef<HTMLDivElement>(null);

  const { ariaLabel, tabIndex, isSelected, isCurrent, onClick, isDisabled } =
    useMonth({
      date,
      itemRef,
    });
  const { disabled, ariaDisabled } = useFormFieldContext();
  const {
    state: { showError },
  } = useFormFieldMessagesContext();
  const highlightColor = isSelected ? 'primary' : 'neutral';
  const validatedHighlightColor =
    showError && highlightColor === 'primary' ? 'critical' : highlightColor;

  return (
    <td
      className={monthFieldStyle({
        color: validatedHighlightColor,
        isDisabled: isDisabled || disabled || ariaDisabled,
      })}
      role="presentation"
    >
      <CalendarItem
        ref={itemRef}
        className="calendar-button calendar-body-button"
        isCurrent={isCurrent}
        isSelected={isSelected}
        isDisabled={isDisabled || disabled}
        highlightColor={validatedHighlightColor}
        tabIndex={tabIndex}
        role="gridcell"
        aria-selected={!!isSelected}
        ariaLabel={ariaLabel}
        view="month"
        onClick={onClick}
        data-dt-children-name="SelectMonth"
        data-dt-properties={`value:${formatDate(date, {
          month: 'numeric',
        })}`}
      >
        {formatDate(date, {
          month: 'short',
        })}
      </CalendarItem>
    </td>
  );
};

Month['displayName'] = 'Month';
